import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cookieManager } from '../../utils/cookieManager';
import { startEvusQuery, getEvusStatus } from '../../services/evusQuery';
import Footer from '../shared/Footer';

const months = [
  { value: '1', label: '一月' },
  { value: '2', label: '二月' },
  { value: '3', label: '三月' },
  { value: '4', label: '四月' },
  { value: '5', label: '五月' },
  { value: '6', label: '六月' },
  { value: '7', label: '七月' },
  { value: '8', label: '八月' },
  { value: '9', label: '九月' },
  { value: '10', label: '十月' },
  { value: '11', label: '十一月' },
  { value: '12', label: '十二月' },
];

export default function EvusQuery() {
  const navigate = useNavigate();
  const [queryType, setQueryType] = useState('enrollment'); // 'enrollment' or 'personal'
  const [form, setForm] = useState({
    passport_number: '',
    visa_number: '',
    birth_day: '1',
    birth_month: '1',
    birth_year: '1990',
    enrollment_number: '',
    country: 'CHINA',
    surname: '',
    given_name: '',
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [taskId, setTaskId] = useState('');
  const [pollingAttempts, setPollingAttempts] = useState(0);

  // 登录检查
  useEffect(() => {
    const authInfo = cookieManager.getAuthInfo();
    if (!authInfo.isLoggedIn) {
      navigate('/auth/login', { replace: true });
    }
  }, [navigate]);

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    setTaskId('');
    setPollingAttempts(0);

    try {
      // 表单验证
      if (!form.passport_number.trim()) {
        throw new Error('请输入护照号码');
      }
      if (!form.visa_number.trim()) {
        throw new Error('请输入B1/B2签证号码');
      }

      if (queryType === 'enrollment') {
        if (!form.enrollment_number.trim()) {
          throw new Error('请输入登记申请号码');
        }
      } else {
        if (!form.surname.trim() || !form.given_name.trim()) {
          throw new Error('请输入姓和名');
        }
      }

      // 启动查询
      console.log('[EVUS] 启动查询:', {
        query_type: queryType,
        ...form,
      });

      const response = await startEvusQuery({
        query_type: queryType,
        passport_number: form.passport_number.trim(),
        visa_number: form.visa_number.trim(),
        birth_day: form.birth_day,
        birth_month: form.birth_month,
        birth_year: form.birth_year,
        enrollment_number: queryType === 'enrollment' ? form.enrollment_number.trim() : undefined,
        country: queryType === 'personal' ? form.country : undefined,
        surname: queryType === 'personal' ? form.surname.trim() : undefined,
        given_name: queryType === 'personal' ? form.given_name.trim() : undefined,
      });

      if (!response.task_id) {
        throw new Error('未获取到任务ID');
      }

      setTaskId(response.task_id);
      console.log('[EVUS] 任务已创建:', response.task_id);

      // 开始轮询结果
      pollStatus(response.task_id);
    } catch (err) {
      const errorMsg = err.message || '查询失败';

      // 处理不同的错误情况
      if (errorMsg.includes('401') || errorMsg.includes('未授权')) {
        setError('请先登录');
        setTimeout(() => navigate('/auth/login'), 2000);
      } else if (errorMsg.includes('402') || errorMsg.includes('积分不足')) {
        setError('❌ 积分不足，请到 积分中心 充值后重试');
      } else {
        setError(errorMsg);
      }

      setLoading(false);
    }
  };

  const pollStatus = async (currentTaskId) => {
    const poll = async (attempt = 0) => {
      try {
        const status = await getEvusStatus(currentTaskId);

        if (!status) {
          setError('无法获取任务状态');
          setLoading(false);
          return;
        }

        setPollingAttempts(attempt + 1);
        console.log(`[EVUS] 轮询 #${attempt + 1}: 状态=${status.status}`);

        if (status.status === 'completed') {
          setResult(status);
          setLoading(false);
          console.log('[EVUS] 查询完成');
          return;
        }

        if (status.status === 'failed') {
          setError(status.error_message || '查询失败');
          setLoading(false);
          console.log('[EVUS] 查询失败:', status.error_message);
          return;
        }

        // 继续轮询（最多轮询60次，即2分钟）
        if (attempt < 60) {
          setTimeout(() => poll(attempt + 1), 2000);
        } else {
          setError('查询超时，请稍后重试');
          setLoading(false);
        }
      } catch (err) {
        console.error('[EVUS] 轮询异常:', err);
        setError(err.message || '查询失败');
        setLoading(false);
      }
    };

    poll();
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-2">
          <Link to="/evus" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            ← EVUS 管理
          </Link>
	        </div>
	        <h1 className="text-3xl font-bold text-gray-900 mb-2">EVUS 状态查询</h1>
	        <p className="text-gray-600 mb-8">查询您的 EVUS 登记状态</p>

	        <div className="mb-6 p-4 rounded-lg bg-amber-50 text-amber-900 border border-amber-200">
	          <span className="font-semibold">隐私提示：</span>
	          本服务后台不保存您的EVUS查询信息与查询结果，请在页面展示结果后自行截图/保存。
	        </div>

	        {/* 错误提示 */}
	        {error && (
	          <div className="mb-6 p-4 rounded-lg bg-red-50 text-red-700 border border-red-200">
	            <span className="font-semibold">❌ </span>
	            {error}
          </div>
        )}

        {/* 查询表单 */}
        {!result && (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            {/* 查询方式切换 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">查询方式</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="queryType"
                    value="enrollment"
                    checked={queryType === 'enrollment'}
                    onChange={(e) => setQueryType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-gray-700">使用登记申请号码</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="queryType"
                    value="personal"
                    checked={queryType === 'personal'}
                    onChange={(e) => setQueryType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-gray-700">使用个人信息</span>
                </label>
              </div>
            </div>

            {/* 必填字段 */}
            <div className="space-y-4 mb-6">
              <h3 className="text-sm font-medium text-gray-900">必填信息</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">护照号码 *</label>
                <input
                  type="text"
                  value={form.passport_number}
                  onChange={(e) => handleInputChange('passport_number', e.target.value)}
                  placeholder="请输入护照号码"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">B1/B2 签证号码 *</label>
                <input
                  type="text"
                  value={form.visa_number}
                  onChange={(e) => handleInputChange('visa_number', e.target.value)}
                  placeholder="请输入B1/B2签证号码"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">出生日期 *</label>
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={form.birth_day}
                    onChange={(e) => handleInputChange('birth_day', e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[...Array(31)].map((_, i) => (
                      <option key={i} value={String(i + 1)}>
                        {i + 1} 日
                      </option>
                    ))}
                  </select>

                  <select
                    value={form.birth_month}
                    onChange={(e) => handleInputChange('birth_month', e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={form.birth_year}
                    onChange={(e) => handleInputChange('birth_year', e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[...Array(100)].map((_, i) => {
                      const year = 2025 - i;
                      return (
                        <option key={year} value={String(year)}>
                          {year} 年
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>

            {/* 条件字段 */}
            <div className="space-y-4 mb-6">
              <h3 className="text-sm font-medium text-gray-900">
                {queryType === 'enrollment' ? '登记申请号码' : '个人信息'}
              </h3>

              {queryType === 'enrollment' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">登记申请号码 *</label>
                  <input
                    type="text"
                    value={form.enrollment_number}
                    onChange={(e) => handleInputChange('enrollment_number', e.target.value)}
                    placeholder="请输入登记申请号码"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">国家 *</label>
                    <select
                      value={form.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CHINA">中国</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">姓 *</label>
                    <input
                      type="text"
                      value={form.surname}
                      onChange={(e) => handleInputChange('surname', e.target.value)}
                      placeholder="请输入姓"
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">名 *</label>
                    <input
                      type="text"
                      value={form.given_name}
                      onChange={(e) => handleInputChange('given_name', e.target.value)}
                      placeholder="请输入名"
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}
            </div>

            {/* 提交按钮 */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed font-medium"
              >
                {loading ? '查询中...' : '提交查询（同意扣除1个积分）'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setForm({
                    passport_number: '',
                    visa_number: '',
                    birth_day: '1',
                    birth_month: '1',
                    birth_year: '1990',
                    enrollment_number: '',
                    country: 'CHINA',
                    surname: '',
                    given_name: '',
                  });
                  setError('');
                  setTaskId('');
                  setPollingAttempts(0);
                }}
                className="px-4 py-2 rounded-lg bg-gray-100 border border-gray-300 hover:bg-gray-200 font-medium"
              >
                重置
              </button>
            </div>
          </form>
        )}

        {/* 查询结果 */}
        {result && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">查询结果</h2>
              <button
                onClick={() => setResult(null)}
                className="px-4 py-2 rounded-lg bg-gray-100 border border-gray-300 hover:bg-gray-200 text-sm font-medium"
              >
                新建查询
              </button>
            </div>

            {result.screenshot_base64 ? (
              <div className="space-y-4">
                <img
                  src={result.screenshot_base64}
                  alt="EVUS查询结果"
                  className="w-full border border-gray-300 rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                  onClick={() => window.open(result.screenshot_base64, '_blank')}
                  title="点击查看大图"
                />
                <p className="text-sm text-gray-500 text-center">点击图片可以查看大图</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">截图加载中...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
      <Footer variant="simple" showCopyright={false} />
    </>
  );
}
